import React, { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Stack,
  Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  AI_COLUMN_FIELDS,
  AI_FIELD_GROUPS,
  AI_FIELD_LABELS,
  AI_FIELD_TO_FORM_KEY,
  AI_LIST_FIELDS,
  AI_TABLE_FIELDS
} from '../constants/aiFieldGroups';

const accentColor = 'var(--wb-accent)';

const normalizeProposedValue = (aiField, aiValue) => {
  if (AI_LIST_FIELDS.has(aiField)) {
    return Array.isArray(aiValue) ? aiValue.filter(Boolean).join(', ') : '';
  }
  return aiValue ? String(aiValue).trim() : '';
};

/**
 * Calcula, para una columna del formulario y su contraparte generada por
 * la IA, la lista de campos donde hay una propuesta distinta al valor
 * actual (vacío o no) -- solo esos campos se muestran, así una columna sin
 * cambios propuestos ni siquiera aparece en el diálogo.
 */
const diffColumn = (formColumn, aiColumn) => {
  if (!aiColumn) {
    return [];
  }
  const diffs = [];
  AI_COLUMN_FIELDS.forEach((aiField) => {
    const formKey = AI_FIELD_TO_FORM_KEY[aiField];
    const proposedValue = normalizeProposedValue(aiField, aiColumn[aiField]);
    if (!proposedValue) {
      return;
    }
    const currentValue = (formColumn[formKey] || '').trim();
    if (currentValue === proposedValue) {
      return;
    }
    diffs.push({ field: aiField, formKey, currentValue, proposedValue });
  });
  return diffs;
};

const buildMergedColumn = (formColumn, diffs) => {
  const merged = { ...formColumn };
  diffs.forEach(({ formKey, proposedValue }) => {
    merged[formKey] = proposedValue;
  });
  return merged;
};

/**
 * Diálogo de revisión de la propuesta de IA en modo edición (ver
 * SemanticTableFormDialog::handleGenerateWithAIClick). A diferencia de la
 * creación (que rellena huecos directo, sin pedir permiso), acá el
 * usuario ya tiene datos reales cargados -- nada se aplica hasta que
 * tilde explícitamente qué campos de tabla y qué columnas completas
 * quiere sobreescribir con la propuesta. `onApply` recibe solo lo
 * aceptado; el componente padre lo mergea en el formulario (el usuario
 * todavía tiene que apretar "Guardar cambios" para persistir).
 */
const MetadataOverridePreviewDialog = ({ open, currentTableFields, currentColumns, metadata, onClose, onApply }) => {
  const [tableFieldChecks, setTableFieldChecks] = useState({});
  const [columnChecks, setColumnChecks] = useState({});

  const tableFieldDiffs = useMemo(() => {
    if (!metadata) {
      return [];
    }
    return AI_TABLE_FIELDS.map(({ key, label }) => {
      const proposedValue = (metadata[key] || '').toString().trim();
      const currentValue = (currentTableFields?.[key] || '').toString().trim();
      return { key, label, currentValue, proposedValue };
    }).filter((item) => item.proposedValue && item.proposedValue !== item.currentValue);
  }, [metadata, currentTableFields]);

  const aiColumnsByName = useMemo(() => {
    const map = new Map();
    (Array.isArray(metadata?.columns) ? metadata.columns : []).forEach((col) => {
      const key = String(col.column_name || '').trim().toLowerCase();
      if (key) {
        map.set(key, col);
      }
    });
    return map;
  }, [metadata]);

  const columnsWithDiffs = useMemo(() => {
    return (currentColumns || [])
      .map((column, index) => {
        const key = String(column.column_name || '').trim().toLowerCase();
        const aiColumn = key ? aiColumnsByName.get(key) : null;
        const diffs = diffColumn(column, aiColumn);
        return { index, column, diffs };
      })
      .filter((entry) => entry.diffs.length > 0);
  }, [currentColumns, aiColumnsByName]);

  // Reinicia la selección cada vez que llega una propuesta nueva -- el
  // usuario empieza siempre sin nada tildado, opt-in explícito. Patrón de
  // React "ajustar estado cuando cambia una prop": comparar contra la
  // referencia anterior y llamar setState en el cuerpo del render.
  const [lastMetadata, setLastMetadata] = useState(metadata);
  if (metadata !== lastMetadata) {
    setLastMetadata(metadata);
    setTableFieldChecks({});
    setColumnChecks({});
  }

  const selectedColumnCount = Object.values(columnChecks).filter(Boolean).length;
  const selectedTableFieldCount = Object.values(tableFieldChecks).filter(Boolean).length;

  const handleToggleTableField = (key) => {
    setTableFieldChecks((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleToggleColumn = (index) => {
    setColumnChecks((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const handleSelectAllColumns = () => {
    const next = {};
    columnsWithDiffs.forEach(({ index }) => {
      next[index] = true;
    });
    setColumnChecks(next);
  };

  const handleDeselectAllColumns = () => setColumnChecks({});

  const handleApply = () => {
    const result = {};
    tableFieldDiffs.forEach(({ key, proposedValue }) => {
      if (tableFieldChecks[key]) {
        result[key] = proposedValue;
      }
    });

    const nextColumns = (currentColumns || []).map((column, index) => {
      const entry = columnsWithDiffs.find((item) => item.index === index);
      if (entry && columnChecks[index]) {
        return buildMergedColumn(column, entry.diffs);
      }
      return column;
    });

    onApply({ ...result, columns: nextColumns });
  };

  const hasAnyDiff = tableFieldDiffs.length > 0 || columnsWithDiffs.length > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          bgcolor: 'var(--wb-bg-surface)',
          color: 'var(--wb-text-strong)',
          borderRadius: 0,
          border: '1px solid var(--wb-x-border-60a5fa26, rgba(96, 165, 250, 0.15))'
        }
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid var(--wb-border)' }}>
        Revisar propuesta de IA
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Stack spacing={3}>
          <Alert severity="info" sx={{ bgcolor: 'var(--wb-x-bg-38bdf814, rgba(56, 189, 248, 0.08))', color: 'var(--wb-text)' }}>
            Tildá los campos de tabla y las columnas que querés actualizar con la propuesta de la IA. Tildar una
            columna aplica todos sus campos de negocio propuestos de una vez, incluso si alguno ya estaba cargado a
            mano -- ningún cambio se guarda todavía, seguís teniendo que apretar &quot;Guardar cambios&quot; al final.
          </Alert>

          {!hasAnyDiff && (
            <Alert severity="warning" sx={{ bgcolor: 'var(--wb-x-bg-f9731614, rgba(249, 115, 22, 0.08))', color: 'var(--wb-text)' }}>
              La IA no propuso ningún cambio distinto a lo que ya está cargado.
            </Alert>
          )}

          {tableFieldDiffs.length > 0 && (
            <Box>
              <Typography variant="subtitle1" sx={{ color: accentColor, fontWeight: 600, mb: 1 }}>
                Campos de tabla
              </Typography>
              <Stack spacing={1}>
                {tableFieldDiffs.map((diff) => (
                  <Box
                    key={diff.key}
                    sx={{
                      border: '1px solid var(--wb-border-20)',
                      p: 1.5
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!tableFieldChecks[diff.key]}
                          onChange={() => handleToggleTableField(diff.key)}
                          sx={{ color: 'var(--wb-muted-60)', '&.Mui-checked': { color: accentColor } }}
                        />
                      }
                      label={<Typography sx={{ color: 'var(--wb-text)', fontWeight: 600 }}>{diff.label}</Typography>}
                    />
                    <Box sx={{ pl: 4 }}>
                      <Typography variant="caption" sx={{ color: 'var(--wb-x-text-e2e8f080, rgba(226, 232, 240, 0.5))', textDecoration: 'line-through' }}>
                        Actual: {diff.currentValue || '—'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: accentColor }}>
                        Propuesto: {diff.proposedValue}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          {columnsWithDiffs.length > 0 && (
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle1" sx={{ color: accentColor, fontWeight: 600 }}>
                  Columnas ({columnsWithDiffs.length} con cambios propuestos)
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button size="small" onClick={handleSelectAllColumns} sx={{ color: 'var(--wb-text-label)' }}>
                    Seleccionar todas
                  </Button>
                  <Button size="small" onClick={handleDeselectAllColumns} sx={{ color: 'var(--wb-text-label)' }}>
                    Deseleccionar todas
                  </Button>
                </Stack>
              </Stack>

              <Stack spacing={1}>
                {columnsWithDiffs.map(({ index, column, diffs }) => (
                  <Accordion
                    key={index}
                    disableGutters
                    sx={{
                      bgcolor: 'var(--wb-x-bg-141b2d, #141b2d)',
                      color: 'var(--wb-text-strong)',
                      border: '1px solid var(--wb-border-20)',
                      '&:before': { display: 'none' }
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'var(--wb-text-label)' }} />}>
                      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: '100%' }}>
                        <FormControlLabel
                          onClick={(event) => event.stopPropagation()}
                          onFocus={(event) => event.stopPropagation()}
                          control={
                            <Checkbox
                              checked={!!columnChecks[index]}
                              onChange={() => handleToggleColumn(index)}
                              sx={{ color: 'var(--wb-muted-60)', '&.Mui-checked': { color: accentColor } }}
                            />
                          }
                          label=""
                          sx={{ mr: 0 }}
                        />
                        <Typography sx={{ fontWeight: 600 }}>{column.column_name}</Typography>
                        <Chip
                          size="small"
                          label={`${diffs.length} campo${diffs.length === 1 ? '' : 's'} con cambios`}
                          sx={{ bgcolor: 'var(--wb-x-bg-38bdf81f, rgba(56, 189, 248, 0.12))', color: accentColor }}
                        />
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        {AI_FIELD_GROUPS.map((group) => {
                          const groupDiffs = diffs.filter((diff) => group.fields.includes(diff.field));
                          if (groupDiffs.length === 0) {
                            return null;
                          }
                          return (
                            <Grid item xs={12} md={6} key={group.key}>
                              <Box
                                sx={{
                                  border: '1px solid var(--wb-x-border-94a3b826, rgba(148, 163, 184, 0.15))',
                                  p: 2,
                                  height: '100%'
                                }}
                              >
                                <Typography variant="subtitle2" sx={{ color: 'var(--wb-text-label)', fontWeight: 600, mb: 1 }}>
                                  {group.label}
                                </Typography>
                                <Stack spacing={1.25}>
                                  {groupDiffs.map((diff) => (
                                    <Box key={diff.field}>
                                      <Typography variant="caption" sx={{ color: 'var(--wb-x-text-e2e8f099, rgba(226, 232, 240, 0.6))' }}>
                                        {AI_FIELD_LABELS[diff.field]}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{ color: 'var(--wb-x-text-e2e8f080, rgba(226, 232, 240, 0.5))', textDecoration: 'line-through' }}
                                      >
                                        {diff.currentValue || '—'}
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: accentColor }}>
                                        {diff.proposedValue}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Stack>
                              </Box>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ borderTop: '1px solid var(--wb-border)', p: 2.5 }}>
        <Typography variant="caption" sx={{ color: 'var(--wb-x-text-e2e8f099, rgba(226, 232, 240, 0.6))', mr: 'auto' }}>
          {selectedColumnCount} de {columnsWithDiffs.length} columna(s) y {selectedTableFieldCount} campo(s) de tabla
          seleccionados
        </Typography>
        <Button onClick={onClose} sx={{ color: 'var(--wb-text-label)' }}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={selectedColumnCount === 0 && selectedTableFieldCount === 0}
          sx={{ bgcolor: accentColor, '&:hover': { bgcolor: 'var(--wb-sky)' } }}
        >
          Aplicar selección
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MetadataOverridePreviewDialog;
